import { useState } from "react";
import Button from "../../../components/button";
import ConfirmDialog, {
  ConfirmDialogType,
} from "../../../components/confirm-dialog";
import DatafieldSection from "../../../components/datafield-section";
import { CommercialLoanDetails } from "../../../dto";
import { FieldLabel, LoanType } from "../../../models";
import { splitKeyWithSpace } from "../../../utils";
import { WriteOffLoanFields } from "../../personal-loan-details/types";
import { refactorCommercialLoanData } from "../helpers";

interface LoanSummaryProps {
  commercialLoanDetails: CommercialLoanDetails | null;
  onWriteOffBtnClick: (
    type: ConfirmDialogType,
    fieldData: WriteOffLoanFields
  ) => void;
}

export default function LoanSummary({
  commercialLoanDetails,
  onWriteOffBtnClick,
}: LoanSummaryProps) {
  const [isWriteOffModalActive, setIsWriteOffModalActive] = useState(false);

  const handleWriteOffSubmit = async (
    type: ConfirmDialogType,
    fieldData: { [x: string]: string }
  ) => {
    onWriteOffBtnClick(type, {
      writeOffDate: fieldData.writeOffDate,
      reason: fieldData.writeOffReason,
    });
  };

  const leftLabelsForLoanSummary: Record<
    Extract<LoanType, "COMMERCIAL_OD">,
    FieldLabel[]
  > = {
    COMMERCIAL_OD: [
      {
        key: "loanType",
        label: "Loan Type :",
        formatMethod: splitKeyWithSpace,
      },
      { key: "sanctionedAmount", label: "Sanctioned Amount :", prefix: "Rs " },
      { key: "irpa", label: "Interest Rate :", suffix: "% PA" },
      { key: "tdsDeducted", label: "TDS Deducted :", prefix: "Rs " },
      {
        key: "drawdownRepaymentPeriodInDays",
        label: "Drawdown Repayment Period In Days :",
      },
      {
        key: "interestRepaymentPeriodInDays",
        label: "Interests Repayment Period In Days :",
      },
    ],
  };

  const rightLabelsForLoanSummary: Record<
    Extract<LoanType, "COMMERCIAL_OD">,
    FieldLabel[]
  > = {
    COMMERCIAL_OD: [
      {
        key: "totalDrawdownAmount",
        label: "Total Drawdown :",
        prefix: " Rs",
      },
      {
        key: "totalOutstandingAmount",
        label: "Total Outstanding :",
        prefix: " Rs",
      },
      {
        key: "totalPrincipalOutstanding",
        label: "Principal O/s:",
        prefix: " Rs",
        formatMethod: (value: number) => Math.max(value, 0).toFixed(2),
      },
      {
        key: "totalInterestOutstanding",
        label: "Interest O/s:",
        prefix: " Rs",
        formatMethod: (value: number) => Math.max(value, 0).toFixed(2),
      },
    ],
  };

  return (
    <>
      <div className="d-flex justify-content-end">
        <Button
          type="button"
          variant="blue-outline"
          onClick={() => setIsWriteOffModalActive(true)}
        >
          Write Off
        </Button>
      </div>
      <DatafieldSection
        title="Loan Summary"
        labels={[
          { items: leftLabelsForLoanSummary["COMMERCIAL_OD"] },
          { items: rightLabelsForLoanSummary["COMMERCIAL_OD"] },
        ]}
        data={refactorCommercialLoanData(commercialLoanDetails)}
      />

      <ConfirmDialog
        active={isWriteOffModalActive}
        title="Write off loan"
        submitButtonText="Write off"
        cancelButtonText="Cancel"
        type="success"
        message={`Are you sure you want to write off this loan? Loan ID: ${commercialLoanDetails?._id}`}
        afterSuccessMessage={`Loan ID: ${commercialLoanDetails?._id} has been written off successfully`}
        customErrorMessage={"Please fill all the fields"}
        onCancel={() => setIsWriteOffModalActive(false)}
        onSubmit={handleWriteOffSubmit}
        inputFields={[
          {
            name: "writeOffDate",
            label: "Write off date",
            type: "date",
            required: true,
            placeholder: "Write off date",
          },
          {
            name: "writeOffReason",
            label: "Write off reason",
            type: "text",
            required: true,
            placeholder: "Write off reason",
          },
        ]}
      />
    </>
  );
}
